import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useOptionalPredictionsContext } from '@src/providers/PredictionsProvider';
import {
  IconName,
  resolvePredictionMarketFields,
  useSecuritiesContext,
  type Column,
  type FilterableProperty,
  type IPredictionOutcomeEvent,
  type IPredictionOutcomeEventSeriesRecord,
  type PredictionMarketLookup,
} from '@talos/kyoko';
import { useMemo } from 'react';

export const PREDICTION_MARKET_BLOTTER_COLUMN_IDS = {
  Series: 'predictionSeries',
  Event: 'predictionEvent',
} as const;

export type PredictionMarketBlotterFilter = {
  SeriesIDs?: string[];
  EventIDs?: string[];
};

export type PredictionMarketBlotterLookup = Required<PredictionMarketLookup>;

const EMPTY_EVENTS_BY_ID = new Map<string, IPredictionOutcomeEvent>();
const EMPTY_SERIES_BY_ID = new Map<string, IPredictionOutcomeEventSeriesRecord>();

export function rowMatchesPredictionMarketBlotterFilters(
  symbol: string | undefined,
  filter: PredictionMarketBlotterFilter,
  lookup: PredictionMarketBlotterLookup
): boolean {
  const seriesIDs = filter.SeriesIDs;
  const eventIDs = filter.EventIDs;
  const hasSeriesFilter = seriesIDs != null && seriesIDs.length > 0;
  const hasEventFilter = eventIDs != null && eventIDs.length > 0;

  if (!hasSeriesFilter && !hasEventFilter) {
    return true;
  }

  const resolved = resolvePredictionMarketFields(symbol, lookup);
  if (hasSeriesFilter && (resolved.seriesID == null || !seriesIDs.includes(resolved.seriesID))) {
    return false;
  }
  if (hasEventFilter && (resolved.eventID == null || !eventIDs.includes(resolved.eventID))) {
    return false;
  }
  return true;
}

export function predictionMarketColIDToFilterKey(id: string): keyof PredictionMarketBlotterFilter | undefined {
  switch (id) {
    case PREDICTION_MARKET_BLOTTER_COLUMN_IDS.Series:
      return 'SeriesIDs';
    case PREDICTION_MARKET_BLOTTER_COLUMN_IDS.Event:
      return 'EventIDs';
    default:
      return undefined;
  }
}

export function usePredictionMarketBlotterLookup(): PredictionMarketBlotterLookup {
  const { securitiesBySymbol } = useSecuritiesContext();
  const predictions = useOptionalPredictionsContext();

  return useMemo(
    () => ({
      securitiesBySymbol,
      predictionEventsByID: predictions?.eventsByID ?? EMPTY_EVENTS_BY_ID,
      predictionEventSeriesByID: predictions?.eventSeriesByID ?? EMPTY_SERIES_BY_ID,
    }),
    [securitiesBySymbol, predictions?.eventsByID, predictions?.eventSeriesByID]
  );
}

export function usePredictionMarketBlotterColumns(): Column[] {
  const { enablePredictionMarkets } = useFeatureFlag();

  return useMemo(() => {
    if (!enablePredictionMarkets) {
      return [];
    }

    return [
      {
        id: PREDICTION_MARKET_BLOTTER_COLUMN_IDS.Series,
        type: 'predictionSeries',
        title: 'Series',
        initialHide: true,
        sortable: true,
      },
      {
        id: PREDICTION_MARKET_BLOTTER_COLUMN_IDS.Event,
        type: 'predictionEvent',
        title: 'Event',
        initialHide: true,
        sortable: true,
      },
    ];
  }, [enablePredictionMarkets]);
}

export function usePredictionMarketBlotterFilterProperties(): FilterableProperty<
  keyof PredictionMarketBlotterFilter
>[] {
  const { enablePredictionMarkets } = useFeatureFlag();
  const predictions = useOptionalPredictionsContext();

  return useMemo(() => {
    if (!enablePredictionMarkets) {
      return [];
    }

    const eventSeries = predictions?.eventSeries ?? [];
    const events = predictions?.events ?? [];
    const eventSeriesByID = predictions?.eventSeriesByID ?? EMPTY_SERIES_BY_ID;
    const eventsByID = predictions?.eventsByID ?? EMPTY_EVENTS_BY_ID;

    return [
      {
        key: 'SeriesIDs',
        label: 'Series',
        icon: IconName.Calendar,
        options: eventSeries.map(series => series.SeriesID),
        getOptionLabel: (seriesID: string) => eventSeriesByID.get(seriesID)?.SeriesName ?? seriesID,
        clearUnsupportedSelectionsOnInit: false,
      },
      {
        key: 'EventIDs',
        label: 'Event',
        icon: IconName.Calendar,
        options: events.map(event => String(event.EventID)),
        getOptionLabel: (eventID: string) => eventsByID.get(eventID)?.EventName ?? eventID,
        clearUnsupportedSelectionsOnInit: false,
        dropdownOptions: {
          dropdownWidth: '380px',
        },
      },
    ];
  }, [enablePredictionMarkets, predictions]);
}
